import { FC, useMemo, useEffect } from 'react'
import * as moment from 'moment'
import styles from 'components/features/pfm/SubscriptionPaymentSourceSection/SubscriptionPaymentSourceSection.styl'
import { Subscription } from 'interfaces/subscription'
import { CreditCardIcon } from 'components/features/pfm/CreditCardIcon/CreditCardIcon'
import TextButton from 'components/atoms/buttons/TextButton'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import Button from 'components/features/pfm/Button/Button'
import { subscriptionSelector } from 'selectors/subscriptionSelectors'
import { cleanupStripePatchActionCreator } from 'actions/stripeActions'

type SubscriptionPaymentSourceSectionProps = Subscription

export const SubscriptionPaymentSourceSection: FC<SubscriptionPaymentSourceSectionProps> = (props) => {
  const { provider, stripe } = props

  const stripeAction = stripe ? stripe.action : null
  const stripeErrorDate = stripe && stripe.action && stripe.last_payment_error_at ? stripe.last_payment_error_at : null

  const dispatch = useStoreDispatch()
  const ctaChild = useMemo(() => {
    const handleClick = () => dispatch(modifyFloatingPaneActionCreator(
      'subscription',
      { mode: 'edit-payment' }
    ))

    if (!stripeAction) {
      return (
        <TextButton size='large' onClick={ handleClick }>Edit</TextButton>
      )
    }

    return (
      <Button
        theme='blue'
        size='mini'
        onClick={ handleClick }
      >
        Update
      </Button>
    )
  }, [dispatch, stripeAction])

  const isSuccess = useStoreSelector((state) => {
    const { isLoading, errorCode, data } = subscriptionSelector(state).stripePatchPaymentMethod
    return !isLoading && !errorCode && !!data
  })

  useEffect(() => {
    return () => {
      if (isSuccess) {
        dispatch(cleanupStripePatchActionCreator())
      }
    }
  }, [isSuccess, dispatch])

  const messageLineChild = useMemo(() => {
    if (isSuccess) {
      return (
        <div className={ styles.successLine }>
          Successfully updated
        </div>
      )
    }

    if (stripeErrorDate) {
      const formattedDate = moment(stripeErrorDate).format('MMMM Do')

      return (
        <div className={ styles.errorLine }>
          Payment failed on { formattedDate }.<br />
          Please update your payment source.
        </div>
      )
    }

    return null
  }, [stripeErrorDate, isSuccess])

  if (provider !== 'stripe' || !stripe) {
    return null
  }

  const { card } = stripe

  return (
    <div className={ styles.subscriptionPaymentSourceSection }>
      <div className={ styles.label }>Payment Source</div>
      <div className={ styles.cardLine }>
        <div className={ styles.brandIconWrapper }>
          <CreditCardIcon brand={ card.brand } className={ styles.brandIcon } />
        </div>
        <div className={ styles.cardNumber }>card ending in { card.last4 }</div>
        <div className={ styles.cta }>
          { ctaChild }
        </div>
      </div>
      { messageLineChild }
    </div>
  )
}
